import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import { getDocks } from '../../services/business/dockService';
import { getDailyReservations } from '../../services/business/reservationService';
import { getVehicleMovementLogsByDateRange } from '../../services/logging/vehicleMovementLogger';
import { VEHICLE_STATUS } from '../../constants/statuses';
import { VEHICLE_MOVEMENT_EVENTS } from '../../constants/systemConfig';
import { getAllFactories } from '../../services/firestore/factoryService';
import { getAllDoorGroups } from '../../services/firestore/doorGroupService';
import { useDataScope } from '../../hooks/useDataScope';
import DockStatusFilter from '../../components/business/DockStatusFilter';
import DockCard from '../../components/business/DockCard';
import '../../styles/components/timeline.css';

const DockStatus = ({ userData }) => {
    const { user } = usePermissions(userData);
    const scope = useDataScope(userData);
    const { error: toastError } = useToast();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [docks, setDocks] = useState([]);
    const [dockStates, setDockStates] = useState({});

    // Filter States
    const [factories, setFactories] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);
    const [filterFactory, setFilterFactory] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [filterDock, setFilterDock] = useState('all');

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const todayStr = new Date().toISOString().split('T')[0];
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);

            const [dockData, resData, logData, factoryData, doorGroupData] = await Promise.all([
                getDocks(user.tenantId),
                getDailyReservations(user.tenantId, todayStr),
                getVehicleMovementLogsByDateRange(user.tenantId, startOfDay, endOfDay),
                getAllFactories(user.tenantId),
                getAllDoorGroups(user.tenantId)
            ]);

            // Enrich docks with factoryId from doorGroups
            const enrichedDocks = dockData.map(dock => {
                const doorGroup = doorGroupData.find(dg => dg.id === dock.doorGroupId);
                return {
                    ...dock,
                    factoryId: doorGroup?.factoryId || null
                };
            });

            // --- DATA SCOPE FILTERING ---
            let filteredFactories = factoryData;
            let filteredGroups = doorGroupData;
            let filteredDocks = enrichedDocks;

            if (!scope.isFullAccess) {
                if (scope.factoryIds.length > 0) {
                    filteredFactories = factoryData.filter(f => scope.factoryIds.includes(f.id));
                }
                if (scope.doorGroupIds.length > 0) {
                    filteredGroups = doorGroupData.filter(g => scope.doorGroupIds.includes(g.id));
                }
                filteredDocks = enrichedDocks.filter(d => {
                    if (scope.dockIds.length > 0 && !scope.dockIds.includes(d.id)) return false;
                    if (scope.doorGroupIds.length > 0 && !scope.doorGroupIds.includes(d.doorGroupId)) return false;
                    if (scope.factoryIds.length > 0 && !scope.factoryIds.includes(d.factoryId)) return false;
                    return true;
                });
            }

            setDocks(filteredDocks);
            setFactories(filteredFactories);
            setDoorGroups(filteredGroups);

            const states = {};
            const activeLogs = {};
            logData.forEach(l => {
                activeLogs[l.reservationId] = l;
            });

            enrichedDocks.forEach(dock => {
                states[dock.id] = { status: 'FREE', vehicle: null, nextReservation: null, totalCount: 0, plannedCount: 0, completedCount: 0 };

                // Bugünün başlangıç ve bitişi
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);

                // Bu dock için TÜM rezervasyonları kontrol et
                const allDockReservations = resData.filter(r => r.dockId === dock.id && r.status !== VEHICLE_STATUS.CANCELLED);

                // BUGÜN için rezervasyonları filtrele
                const todaysReservations = allDockReservations.filter(r => {
                    const resDate = new Date(r.startTime);
                    return resDate >= today && resDate < tomorrow;
                });

                const totalCount = allDockReservations.length; // Toplam rezervasyon
                const plannedCount = todaysReservations.length; // Bugünkü planlar
                const completedCount = todaysReservations.filter(r =>
                    r.status === VEHICLE_STATUS.LOADING_COMPLETED || r.status === 'completed'
                ).length; // Bugün tamamlananlar

                states[dock.id].totalCount = totalCount;
                states[dock.id].plannedCount = plannedCount;
                states[dock.id].completedCount = completedCount;

                if (dock.status === 'maintenance') {
                    states[dock.id].status = 'MAINTENANCE';
                    return;
                }

                const activeRes = resData.find(r => {
                    if (r.dockId !== dock.id) return false;
                    const log = activeLogs[r.id];
                    if (!log) return false;
                    return [VEHICLE_MOVEMENT_EVENTS.DOCK_ARRIVAL, VEHICLE_MOVEMENT_EVENTS.LOADING_START].includes(log.currentStatus);
                });

                if (activeRes) {
                    const log = activeLogs[activeRes.id];
                    states[dock.id].status = log.currentStatus === VEHICLE_MOVEMENT_EVENTS.LOADING_START ? 'LOADING' : 'OCCUPIED';
                    states[dock.id].vehicle = activeRes;
                    states[dock.id].log = log;
                } else {
                    const now = new Date();
                    const nextRes = resData
                        .filter(r => r.dockId === dock.id && new Date(r.startTime) > now && r.status !== VEHICLE_STATUS.CANCELLED)
                        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))[0];

                    if (nextRes) {
                        states[dock.id].nextReservation = nextRes;
                    }
                }
            });

            setDockStates(states);
        } catch (err) {
            toastError('Veri yükleme hatası: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'FREE': return '#22c55e';
            case 'OCCUPIED': return '#f59e0b';
            case 'LOADING': return '#3b82f6';
            case 'MAINTENANCE': return '#ef4444';
            default: return '#94a3b8';
        }
    };

    return (
        <div className="container-fluid pt-lg">
            {/* Header & Filters */}
            <div className="flex flex-col gap-6 mb-8">
                <div>
                    <h1>Rampa Durumları</h1>
                </div>

                {/* FILTER BAR - ReservationManagement Style */}
                <div className="card-premium glow-theme" style={{ padding: '1.25rem 1.5rem' }}>
                    <DockStatusFilter
                        filterFactory={filterFactory}
                        setFilterFactory={setFilterFactory}
                        filterType={filterType}
                        setFilterType={setFilterType}
                        filterDock={filterDock}
                        setFilterDock={setFilterDock}
                        factories={factories}
                        doorGroups={doorGroups}
                        docks={docks}
                    />
                </div>
            </div>

            {/* Dock List */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 border-4 border-t-transparent border-green-500 rounded-full animate-spin" />
                        <div className="text-xl font-bold text-secondary">
                            Yükleniyor...
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {docks
                        .filter(dock => {
                            // Apply filters
                            if (filterFactory !== 'all' && dock.factoryId !== filterFactory) return false;
                            if (filterType !== 'all' && dock.doorGroupId !== filterType) return false;
                            if (filterDock !== 'all' && dock.id !== filterDock) return false;
                            return true;
                        })
                        .sort((a, b) => {
                            // Natural sort for names like "Gate 1", "Gate 2", etc.
                            const nameA = a?.name || '';
                            const nameB = b?.name || '';
                            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
                        })
                        .map(dock => {
                            const state = dockStates[dock.id] || { status: 'FREE', plannedCount: 0 };
                            const color = getStatusColor(state.status);

                            return (
                                <DockCard
                                    key={dock.id}
                                    dock={dock}
                                    state={state}
                                    color={color}
                                    onManage={() => navigate(`/dock/management?dockId=${dock.id}`)}
                                    onViewDetails={() => navigate(`/dock/status/${dock.id}`)}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
};

export default DockStatus;
